import { formatNumber, useLocale, useTranslation } from '@multaqa/i18n'
import { Alert, AlertDescription, AlertTitle } from '@multaqa/ui/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import type { ReactNode } from 'react'
import type { ErasurePlan } from './api'

const BLOCKER_KINDS = ['cross_group_link', 'demo_group', 'open_support_session'] as const

/**
 * One size in the largest unit that keeps it at or above 1. Bytes go through
 * the plural keys ("0 bytes", "1 byte"); larger units through Intl.
 */
function useFormatBytes(): (bytes: number) => string {
  const { t } = useTranslation()
  const { locale } = useLocale()
  return (bytes) => {
    if (bytes < 1024) {
      return t('groups.detail.erasure.bytes', { count: bytes, value: formatNumber(bytes, locale) })
    }
    const units = ['kilobyte', 'megabyte', 'gigabyte'] as const
    let value = bytes / 1024
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024
      unit++
    }
    return formatNumber(value, locale, {
      style: 'unit',
      unit: units[unit],
      unitDisplay: 'short',
      maximumFractionDigits: 1,
    })
  }
}

/**
 * What erasure would delete for one group, as the dry run counted it: totals,
 * blockers, the audit and tax-record notes (O4, O8), then rows per table in
 * delete order and files per folder.
 */
export function ErasurePlanView({ plan }: { plan: ErasurePlan }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const rows = plan.tables.reduce((sum, table) => sum + table.rows, 0)
  const files = plan.files.reduce((sum, folder) => sum + folder.count, 0)
  const n = (value: number) => formatNumber(value, locale)
  const formatBytes = useFormatBytes()

  return (
    <div className="space-y-4" data-testid="group-erasure-plan">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
        <Fact label={t('groups.detail.erasure.totals.rows')}>{n(rows)}</Fact>
        <Fact label={t('groups.detail.erasure.totals.tables')}>{n(plan.tables.length)}</Fact>
        <Fact label={t('groups.detail.erasure.totals.files')}>{n(files)}</Fact>
        <Fact label={t('groups.detail.erasure.totals.users')}>{n(plan.users)}</Fact>
      </dl>

      {plan.blockers.length === 0 ? (
        <Alert severity="success" data-testid="group-erasure-no-blockers">
          <AlertTitle>{t('groups.detail.erasure.noBlockers')}</AlertTitle>
        </Alert>
      ) : (
        <Alert severity="danger" data-testid="group-erasure-blockers">
          <AlertTitle>{t('groups.detail.erasure.blockersTitle')}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 ps-4">
              {plan.blockers.map((blocker) => (
                <li key={`${blocker.kind}-${blocker.detail}`}>
                  {(BLOCKER_KINDS as readonly string[]).includes(blocker.kind)
                    ? t(`groups.detail.erasure.blockers.${blocker.kind}`)
                    : blocker.kind}{' '}
                  <bdi className="text-identifier font-mono">{blocker.detail}</bdi>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Alert severity="info" data-testid="group-erasure-audit-note">
        <AlertTitle>{t('groups.detail.erasure.auditTitle')}</AlertTitle>
        <AlertDescription>
          {t('groups.detail.erasure.auditBody', {
            kept: n(plan.audit_security_kept),
            deleted: n(plan.audit_financial_deleted),
          })}
        </AlertDescription>
      </Alert>
      <Alert severity="warning" data-testid="group-erasure-tax-note">
        <AlertTitle>{t('groups.detail.erasure.taxTitle')}</AlertTitle>
        <AlertDescription>{t('groups.detail.erasure.taxBody')}</AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-2">
        <CountTable
          testId="group-erasure-tables"
          head={[t('groups.detail.erasure.columns.table'), t('groups.detail.erasure.columns.rows')]}
          rows={plan.tables.map((table) => [table.name, n(table.rows)])}
        />
        <CountTable
          testId="group-erasure-files"
          head={[
            t('groups.detail.erasure.columns.folder'),
            t('groups.detail.erasure.columns.files'),
            t('groups.detail.erasure.columns.size'),
          ]}
          rows={plan.files.map((folder) => [
            folder.folder,
            n(folder.count),
            formatBytes(folder.bytes),
          ])}
        />
      </div>
    </div>
  )
}

function CountTable({ head, rows, testId }: { head: string[]; rows: string[][]; testId: string }) {
  return (
    <div className="overflow-x-auto">
      <Table className="border border-border" data-testid={testId}>
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            {head.map((label, index) => (
              <TableHead key={label} className={index === 0 ? undefined : 'text-end'}>
                {label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((cells) => (
            <TableRow key={cells[0]}>
              {cells.map((cell, index) => (
                <TableCell
                  // biome-ignore lint/suspicious/noArrayIndexKey: a row's cells are positional columns.
                  key={index}
                  className={
                    index === 0
                      ? 'text-identifier font-mono whitespace-normal break-all'
                      : 'text-end tabular-nums whitespace-nowrap'
                  }
                >
                  {index === 0 ? <bdi>{cell}</bdi> : cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-small text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-body tabular-nums">{children}</dd>
    </div>
  )
}
